import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
} from '@hh/global.js'

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    vertical: {
      reflect: true,
    },
    dots: {
      reflect: true,
    },
    _advance: {
      state: true,
    },
    _scrollDirection: {
      state: true,
    },
    _direction: {
      state: true,
    },
    'slide-width': {
      reflect: true,
    },
    reverse: {
      reflect: true,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this._advance = 0

    this.updateUI = this.updateUI.bind(this)
    this.scrollTo = this.scrollTo.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    setTimeout(() => {
      this.updateUI()
      this.$container?.value?.addEventListener('scroll', this.updateUI)
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }
      this.addEventListener('hh-slider:scroll-to', this.scrollTo)
      if (this['slide-width']) {
        const activeItemIndex = Array.from(
          this.querySelectorAll('[slot="item"]')
        ).findIndex((item) => item.classList.contains('is-active'))
        if (!~activeItemIndex) return
        this.move(null, activeItemIndex)
      }
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.removeEventListener('hh-slider:scroll-to', this.scrollTo)
    this.observer?.disconnect()
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }
  }

  updateUI() {
    if (!this._total || !this.$container.value) {
      return
    }

    this._advance = this.isVertical()
      ? this.items[0].clientHeight
      : this.items[0].clientWidth
    this._scrollDirection = this.isVertical() ? 'scrollTop' : 'scrollLeft'
    this._direction = this.isVertical() ? 'top' : 'left'
    this._current =
      Math.round(this.$container.value[this._scrollDirection] / this._advance) +
      1
  }

  move(event, index = false) {
    event?.preventDefault()
    if (!this.$container.value) return
    const reverseIndex = this.reverse === 'true' ? -1 : 1
    const slideScrollPosition =
      index !== false
        ? this._advance * index
        : event.currentTarget.name === 'next'
          ? this.$container.value[this._scrollDirection] + this._advance
          : this.$container.value[this._scrollDirection] - this._advance
    if (!this.active) {
      const mediaOffsetTop = this.items.find(
        (item) => Number(item.dataset.index) === index
      ).offsetTop
      return window.scrollTo({
        top: mediaOffsetTop,
        behavior: 'smooth',
      })
    }
    this.$container?.value?.scrollTo({
      [this._direction]: slideScrollPosition * reverseIndex,
    })
  }

  scrollTo(event) {
    this.move(null, event.detail.index)
  }

  isVertical() {
    return [true, 'true'].includes(this.vertical)
  }

  showDots() {
    return [true, 'true'].includes(this.dots)
  }

  renderDot(index) {
    return html`
      <li>
        <button
          class="${index === this._current - 1 ? 'is-current' : ''}"
          @click="${(event) => this.move(event, index)}"
        >
          <span class="visually-hidden">${index}</span>
        </button>
      </li>
    `
  }

  renderDots() {
    if (!this.showDots()) {
      return html``
    }
    return html`<div class="dots">
      <ul role="list">
        ${[...Array(this._total).keys()].map((index) => this.renderDot(index))}
      </ul>
    </div>`
  }

  render() {
    return html`
      <div class="container" ${ref(this.$container)} role="list">
        <slot name="item"></slot>
      </div>
      <div class="buttons">
        <div class="counter">
          <span class="counter--current">${this._current}</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
          <span class="counter--total">${this._total ? this._total : ''}</span>
        </div>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          ?disabled="${this._current === 1}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
          ?disabled="${this.__current === this._total}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
      </div>
      ${this.renderDots()}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host([active='true']) {
        display: block;
        position: relative;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
      }

      :host([scroll-bar='false']) .container::-webkit-scrollbar {
        display: none;
      }

      :host([active='true'][slide-width='variable']) .container {
        gap: 1rem;
        padding-bottom: 1rem;
      }

      @media screen and (min-width: 750px) {
        :host([active='true'][slide-width='variable']) .container {
          gap: 5rem;
        }
      }

      :host([active='true'][reverse='true']) .container {
        flex-direction: row-reverse;
      }

      :host([active='true'][alignLeft='true']) {
        display: flex;
      }

      :host([active='true'][alignLeft='true']) .container {
        width: auto;
      }

      :host([active='false']) .container {
        gap: 0.5rem;
      }

      :host([active='true'][vertical='true']) .container {
        aspect-ratio: var(--hh-vertical-aspect-ratio);
        flex-wrap: wrap;
        overflow-y: auto;
        overflow-x: hidden;
        scroll-snap-type: y mandatory;
      }

      :host([active='true']) ::slotted(*) {
        flex: 1 0 100%;
        scroll-snap-align: start;
      }

      :host([active='true'][slide-width='variable']) ::slotted(*) {
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
      }

      :host([active='false']) ::slotted(*) {
        flex: 1 0 100%;
      }

      :host([active='false']) ::slotted(*:nth-child(3n + 2)),
      :host([active='false']) ::slotted(*:nth-child(3n + 3)) {
        flex: 1 0 calc(50% - 0.5rem);
      }

      .buttons {
        display: none;
        align-items: center;
        position: absolute;
        z-index: 2;
        right: 0;
        bottom: 0;
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      :host([active='true'][buttons='true']) .buttons {
        display: flex;
      }

      .dots {
        display: none;
        position: absolute;
        z-index: 3;
        bottom: 20px;
        left: 0;
        right: 0;
      }

      :host([vertical='true']) .dots {
        top: 50%;
        bottom: auto;
        right: auto;
        left: 10px;
        transform: translateY(-50%);
      }

      :host([active='true'][dots='true']) .dots {
        display: block;
      }

      .dots ul {
        list-style-type: none;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;
      }

      :host([vertical='true']) .dots ul {
        display: block;
      }

      .dots li {
        margin: 0 5px;
      }

      :host([vertical='true']) .dots li {
        margin: 5px 0;
      }

      .dots button {
        background: none;
        width: 5px;
        height: 5px;
        padding: 0;
        border: 1px solid var(--hh-color-brand-tertiary);
        border-radius: 0;
        position: relative;
        z-index: 1;
      }

      .dots button.is-current {
        background: var(--hh-color-brand-tertiary);
        border-radius: 50%;
      }

      .counter {
        margin: 0 2rem;
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: var(--hh-color);
        border: none;
        background: var(--hh-button-background);
      }

      .button svg {
        height: 0.6rem;
      }

      .button--prev {
        margin-left: -0.2rem;
        transform: rotate(90deg);
      }

      .button--next {
        margin-left: -0.2rem;
        transform: rotate(-90deg);
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)
